<template>
  <div class="app-main">
    <match-finder-configuration-panel
      v-if="showMatchFinder"
      :name="this.currentFace.person_name"
      :frontend-face-u-u-i-d="this.currentFace.frontend_face_uuid"
      @close="showMatchFinder = !showMatchFinder"
      />
    <div
      v-if="isAuthenticated"
      class="wrapper"
      >
      <delete-face
        v-if="this.showDelete"
        :name="this.currentFace.person_name"
        :face-id="this.currentFace.frontend_face_uuid"
        @close="handleButton"
        />
      <add-face
        v-if="this.showEdit"
        :face-to-edit="this.currentFace"
        @close="handleButton"
        />
      <owal-panel
        :key="`${this.currentFace ? this.currentFace.frontend_face_uuid : ''}`"
        :header-component="this.facesHeader"
        :header-props="headerProps"
        :title="currentFace ? '':'Welcome to Owal Faces'"
        :class="`round-card${this.currentFace ? '_with-face' : ''}`"
        :show-top-banner="!!this.currentFace"
        >
        <div
          v-if="this.currentFace"
          style="height: 100%; width: 100%; overflow: visible; flex-grow: 10;"
          >
          <div slot="content">
            <div :class="`last-seen__container-outer${this.confirmed ? '_confirmed' : ''}`">
              <div class="last-seen__container-inner">
                <transition
                  name="poster"
                  appear
                  >
                  <owal-event-banner-poster-frame
                    v-show="currentFace && showPosterFrame && currentFace.last_seen_preview"
                    :key="currentFace.last_seen_frame_uri"
                    style="position: relative"
                    :poster-frame-small="currentFace.last_seen_preview"
                    :poster-frame-large="currentFace.last_seen_frame_uri"
                    :title="lastLocation"
                    :subtitle="lastSeenTimestamp"
                    :buttons="buttonsFiltered"
                    :detectionId="currentFace.last_seen_detection_id"
                    />
                </transition>
                <div class="content">
                  <div class="header">
                    {{ lastLocation }}
                  </div>
                  <div class="subheader">
                    {{ lastSeenTimestamp }}
                  </div>
                </div>
                <!-- close header -->
              </div>
              <div
                id="navigation"
                class="content"
                >
                <span
                  v-for="tab in tabs"
                  :key="tab.name"
                  type="text"
                  class="button"
                  :class="`owal-button-${cssClassFromTabName(tab.name)}__container`"
                  @click="tab.callback"
                  >
                  <span :class="tab.class" />
                  {{ tab.name }}
                </span>
              </div>
            </div>
            <!-- close last-seen__container-outer -->
            <transition name="history" appear>
              <div v-if="this.showHistory">
                <faces-histogram :currentFaceUUID="currentFace.frontend_face_uuid" />
              </div>
            </transition>
            <transition name="history" appear>
              <div v-if="this.showConfirmed">
                <confirmed :faceId="this.currentFace.frontend_face_uuid"/>
              </div>
            </transition>
            <transition name="history" appear>
              <div v-if="this.showAnomaly">
                <anomaly-list :frontendFaceUUID="this.currentFace.frontend_face_uuid"/>
              </div>
            </transition>
          </div>
        </div>
        <div v-else>
          Browse the list of people on the left. Click on a name to see where they were last seen. Tip: Check out the Filter feature for quick lookup!
          <br>To help us train the detector, click
          <router-link to="/detected">
            Detections
          </router-link>. It becomes more accurate with every click!
        </div>
      </owal-panel>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import DeleteFace from './DeleteFace'
import AddFace from './AddFace'
import moment from 'moment'
import { titleCase } from '@/libraries/util/string'
import MatchFinderConfigurationPanel from '@/components/faces-system/match-finder/MatchFinderConfigurationPanel'
import { PosterFrameButtons, Identifiers } from '@/mixins'
import FacesHistogram from './FacesHistogram'
import FacesDetailsProfileHeader from './FacesDetailsProfileHeader'
import { OwalPanel } from '@/libraries/owal-ui'
import Confirmed from './Confirmed'
import AnomalyList from './AnomalyList'

import OwalEventBannerPosterFrame from '@/libraries/owal-ui/banners/OwalEventBannerPosterFrame'
import { Service, Operation } from '@/libraries/permissions'

export default {
  name: 'FacesDetails',
  components: {
    DeleteFace,
    AddFace,
    OwalPanel,
    FacesHistogram,
    OwalEventBannerPosterFrame,
    MatchFinderConfigurationPanel,
    Confirmed,
    AnomalyList,
  },
  mixins: [PosterFrameButtons],
  data () {
    return {
      showDelete: false,
      showEdit: false,
      showPosterFrame: true,
      showHistory: false,
      FacesDetailsProfileHeader,
      removed: false,
      showMatchFinder: false,
      showConfirmed: false,
      showAnomaly: false,
    }
  },
  computed: {
    ...mapGetters({
      frontendFaces: 'frontendFaces',
      isAuthenticated: 'isAuthenticated',
      canPerformOperationForCurrentService: 'entitlements/canPerformOperationForCurrentService',
      canPerformOperationForService: 'entitlements/canPerformOperationForService',
      isRefreshingService: 'isRefreshingService',
      currentDomainUUID: 'currentDomainUUID',
    }),

    lastLocation () {
      if (this.removed) {
        return ''
      }
      return titleCase(this.currentFace.last_seen_camera_description)
    },
    lastSeenTimestamp () {
      if (this.removed) {
        return 'Not Seen Recently'
      }

      const lastSeenTimestamp = this.currentFace.last_seen_datetime

      if (lastSeenTimestamp) {
        return moment(lastSeenTimestamp)
          .local()
          .format('ddd, MMMM Do YYYY, h:mm A')
      }

      return 'Not Seen Recently'
    },
    buttonsFiltered () {
      const buttons = this.buttons
      const canAccessVideo = this.canAccessVideo
      const filteredButtons = buttons.filter(
        button => button.identifier !== Identifiers.VIDEO || canAccessVideo
      )
      return filteredButtons
    },
    headerProps () {
      return {
        currentIdentity: this.currentFace,
        tags: this.tags,
      }
    },
    facesHeader () {
      const header = this.FacesDetailsProfileHeader
      return header
    },
    currentFace () {
      if (!this.$route) {
        return undefined
      }

      const id = this.$route.params.id
      if (!id) {
        return undefined
      }

      const face = this.frontendFaces.find(
        face => face.frontend_face_uuid === id
      )
      return face
    },
    tags () {
      let tags = []

      if (this.currentFace) {
        if (this.currentFace.employee) {
          tags.push('Staff')
        }

        if (this.currentFace.resident) {
          tags.push('Resident')
        }

        if (this.currentFace.role) {
          const roleValueSnakeCase = this.currentFace.role
          const role = titleCase(roleValueSnakeCase.replace('_', ' '))
          tags.push(role)
        }

        if (this.currentFace.elopement_risk) {
          tags.push('Elopement Risk')
        }

        if (this.currentFace.room) {
          tags.push('Room ' + this.currentFace.room)
        }

        if (this.currentFace.unit) {
          tags.push(`Unit ${this.currentFace.unit}`)
        }

        if (this.currentFace.bed) {
          tags.push(this.currentFace.bed + ' Bed')
        }

        if (this.currentFace.wing) {
          tags.push(this.currentFace.wing + ' Wing')
        }
      }

      return tags
    },
    isUpdatingHistory () {
      return this.isRefreshingService('history')
    },
    historyButtonTitle () {
      let historyButtonTitle = 'Show History'
      if (this.isUpdatingHistory) {
        historyButtonTitle = 'Loading History...'
      } else {
        if (this.showHistory) {
          historyButtonTitle = 'Hide History'
        } else if (this.entireHistoryLength === 0) {
          historyButtonTitle = 'No History'
        }
      }

      return historyButtonTitle
    },
    tabs () {
      if (!this.currentFace) {
        return []
      }

      const id = this.currentFace.frontend_face_uuid

      if (!id) {
        return []
      }

      let tabs = [
        {
          name: 'Most Recent Detection',
          class: 'green dot',
          callback: function () {
            this.showPosterFrame = !this.showPosterFrame // eslint-disable-line

            if (!this.showPosterFrame) {
              this.showHistory = false // eslint-disable-line
            }
          }.bind(this),
        },
        {
          name: this.historyButtonTitle,
          class: 'history icon',
          callback: function () {
            // Don't toggle if loading in progress
            if (this.isUpdatingHistory) {
              if (this.entireHistoryLength === 0) {
                return
              }
            }

            if (!this.showPosterFrame) {
              this.showHistory = false // eslint-disable-line
            }
            if (this.showConfirmed) {
              this.showConfirmed = false // eslint-disable-line
            }
            this.showHistory = !this.showHistory // eslint-disable-line
          }.bind(this),
          condition: this.showPosterFrame,
        },
        {
          name: this.currentFace.images_tagged + ' Confirmed Matches',
          class: 'tags icon',
          callback: function () {
            if (this.showHistory) {
              this.showHistory = false // eslint-disable-line
            }
            this.showConfirmed = !this.showConfirmed // eslint-disable-line
          }.bind(this),
        },
        {
          name: 'More Detections',
          class: 'verify icon',
          callback: function () {
            this.showMatchFinder = true // eslint-disable-line
          }.bind(this),
        },
        {
          name: 'Anomalies',
          class: 'anomaly icon',
          callback: function () {
            this.showHistory = false // eslint-disable-line
            this.showConfirmed = false // eslint-disable-line
            this.showAnomaly = !this.showAnomaly // eslint-disable-line
          }.bind(this),
        },
      ]
      if (this.canPerformOperationForCurrentService('add')) {
        const editButton = {
          name: 'Edit',
          class: 'edit icon',
          callback: function () {
            this.showEdit = true // eslint-disable-line
          }.bind(this),
        }
        tabs.push(editButton)
      }
      if (this.canPerformOperationForCurrentService('delete')) {
        const deleteButton = {
          name: 'Delete',
          class: 'delete icon',
          callback: function () {
            this.showDelete = true // eslint-disable-line
          }.bind(this),
        }
        tabs.push(deleteButton)
      }

      return tabs.filter(
        tab => tab.condition === undefined || tab.condition === true
      )
    },
    context () {
      const context = Object.assign(this.defaultContext, {
        video: {
          timestamp: moment(this.currentFace.last_seen_datetime)
            .utc()
            .format(),
          cameraUUID: this.currentFace.last_seen_camera_uuid,
        },
        face: {
          frontendUUID: this.currentFace.frontend_face_uuid,
          confirmed: this.confirmed,
          detectionId: this.currentFace.last_seen_detection_id,
          domainUUID: this.currentDomainUUID,
        },
        handlers: {
          confirmFace: this.didConfirm,
          notAFace: this.didRemove,
          undoConfirm: this.didConfirm,
          error: this.handleError,
          notAMatch: this.didRemove,
        },
      })
      return context
    },
    canAccessVideo () {
      const canAccessHistoricalVideo = this.canPerformOperationForService(
        Service.VIDEO,
        Operation.VIEW_HISTORIC
      )
      return canAccessHistoricalVideo
    },
  },
  methods: {
    ...mapActions({
      loadFrontendFaces: 'loadFrontendFaces',
      updateLastSeen: 'updateLastSeen',
      fetchUpdatedHistory: 'facesModule/fetchUpdatedHistory',
      getCurrentModel: 'trainingTool/getCurrentModel',
    }),
    ...mapMutations({
      incrementRequestCountForServiceNamed: 'incrementRequestCountForServiceNamed',
      decrementRequestCountForServiceNamed: 'decrementRequestCountForServiceNamed',
      removeEventFromCachedHistory: 'facesModule/removeEventFromCachedHistory',
    }),
    cssClassFromTabName: function (tabName) {
      // The \ before the - is strictly unnecessary, but makes the regexp easier to read.
      // eslint-disable-next-line
      return tabName.toLowerCase().replace(' ', '-').replace(/[^0-9a-z\-]/gi, '')
    },
    showVideo () {
      if (this.canAccessVideo) {
        const lastSeenCameraId = this.currentFace.last_seen_camera_uuid
        const lastSeenTimestamp = this.currentFace.last_seen_datetime
        if (lastSeenCameraId) {
          const lastSeenPreview = `/view/${lastSeenCameraId}/${lastSeenTimestamp}`
          this.$router.push({
            path: lastSeenPreview,
          })
        }
      }
    },
    handleButton: function (payload) {
      // Delete faces - probably need better encapsulation here. Maybe pass an ID.
      this.showDelete = false
      this.showEdit = false
      const success = !!payload.success
      const action = payload.action

      if (success && action) {
        if (['edit', 'add'].includes(action)) {
          // console.log(`${action} triggered a reload.`)
          this.loadFrontendFaces()
        }
      }
    },
    didConfirm (confirm) {
      this.confirmed = confirm
      this.context.face.confirmed = confirm
      // this.removed = confirm === false // Remember that undefined is valid here.
      this.updateLastSeen()
    },
    didRemove () {
      /**
       * Wrong Person or Not a Face
      */
      this.confirmed = false
      // this.removed = true
      const payload = {
        frontendFaceUUID: this.currentFace.frontend_face_uuid,
        detectionId: this.currentFace.last_seen_detection_id,
      }
      this.removeEventFromCachedHistory(payload)
      this.updateLastSeen()
    },
    handleError (error) {
      console.error(error)
    },
  },
  mounted () {
    this.getCurrentModel()
  },
  watch: {
    currentDomainUUID (oldDomainUUID, newDomainUUID) {
      this.getCurrentModel()
    },
    currentFace: {
      deep: true,
      handler: function (newFace, oldFace) {
        // When Faces is in default (i.e. https://.../faces/:id) this polls the API with `https://...API/faces/matched/undefined`
        if (this.currentFace !== undefined) {
          const isSamePerson =
            oldFace &&
            newFace &&
            newFace.frontend_face_uuid === oldFace.frontend_face_uuid

          if (!isSamePerson) {
            this.showHistory = false
            this.confirmed = undefined
            this.removed = false
            this.fetchUpdatedHistory()
          }
        }
      },
    },
  },
}
</script>

<style scoped>
@media only screen and (min-width: 768px) {
  .owal.modal.form.style .owal.panel.active.window,
  .owal.modal.form.style .owal.panel.inactive.window {
    max-width: 50rem;
    max-height: 80%;
  }
}

@media only screen and (max-width: 768px) {
}

.app-main {
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-content: center;
}

.wrapper {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-content: center;
  flex-grow: 10;
}

.wrapper >>> .owal.panel.container {
  flex-grow: 10;
  max-height: 100%;
}

.owal-logo {
  height: 40px;
  width: 160px;
}

.round-card_with-face,
.round-card {
  overflow: auto;
  padding: 0;
  flex-basis: content;
}

.round-card {
  width: 61rem;
  max-height: 80%;
}

.round-card_with-face {
  width: 100%;
  height: 100%;
  overflow: auto;
}

.last-seen__container-inner {
  display: flex;
  flex-direction: row;
  align-content: flex-center;
  justify-content: flex-start;
  align-items: center;
  transition: all 250ms;
}

.last-seen__container-inner .content {
  padding-left: 1rem;
}

.last-seen__container-inner
  >>> .owal-poster-frame__image-large-outer-container {
  margin-top: 5rem;
}

.last-seen__container-outer,
.last-seen__container-outer_confirmed {
  border-radius: 20px;
  padding: 32px;
  margin: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 250ms;
}

.last-seen__container-outer {
  background-color: rgba(216, 216, 216, 0.25);
}

.last-seen__container-outer_confirmed {
  background: lightgreen;
}

.last-seen__container-outer .header,
.last-seen__container-outer_confirmed .header {
  font-size: 1.75rem;
  line-height: 2rem;
  font-family: 'Lato-Bold', 'Helvetica-Bold', 'Helvetica Bold', 'Helvetica';
}

.last-seen__container-outer .subheader,
.last-seen__container-outer_confirmed .subheader {
  color: #69686a;
  font-size: 1rem;
  line-height: 1.3rem;
  font-family: 'Lato', 'Helvetica';
  max-width: 41.4285714286rem;
}

.last-seen__container-outer
  #navigation
  .last-seen__container-outer_confirmed
  #navigation {
  align-self: flex-end;
  display: flex;
  background-color: none;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  align-content: flex-start;
}

.button {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 2.0714285714rem;
  width: 100%;
  cursor: pointer;

  padding: 0;
  margin: 0 5px;

  font-size: 0.8571428571rem;
  font-family: 'Lato', 'Helvetica';

  border-radius: 10px;

  color: #9b9b9b;
}

.button:hover,
.button:active {
  background-color: #d8d8d8;
}

.last-seen__container-innerter-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 2.0714285714rem;
  width: fit-content;
  cursor: pointer;

  padding: 0;
  margin: 0 5px;

  font-size: 0.8571428571rem;
  font-family: 'Lato', 'Helvetica';

  border-radius: 10px;

  color: #9b9b9b;
}

.dot {
  display: inline-block;
  height: 10px;
  width: 10px;
  border-radius: 5px;
  border: 0.5px solid #979797;
  margin: 0 6px;
  padding: 0;
}

.green {
  background-color: #7ed321;
}

.yellow {
  background-color: #f8e71c;
}

.red {
  background-color: #d0021b;
}

*.icon {
  height: 13px;
  width: 13px;
  margin: 0 6px 0 5px;
  background-size: 100%;
  background-color: none;
  background-repeat: no-repeat;
  background-position: center;
  overflow: visible;
}

#delete {
  cursor: pointer;
}

.delete.icon {
  background-image: url('../../libraries/owal-ui/assets/img/glyphs/delete@3x.png');
  background-size: 13px 13px;
}

.tags.icon {
  background-image: url('../../libraries/owal-ui/assets/img/glyphs/tag@3x.png');
  background-size: 13px 13px;
}

.verify.icon {
  background-image: url('../../libraries/owal-ui/assets/img/glyphs/magnify@3x.png');
  background-size: 13px 13px;
}

.anomaly.icon {
  background-image: url('../../libraries/owal-ui/assets/img/glyphs/ai.png');
  background-size: 13px 13px;
}

.edit.icon {
  background-image: url('../../libraries/owal-ui/assets/img/glyphs/edit@3x.png');
  background-size: 13px 13px;
}

.poster.frame {
  background-color: #000;
  height: 6.4271428571rem;
  width: 11.4371428571rem;
  object-fit: contain;
  margin: 0 1rem 0 0;
  cursor: pointer;
  border: 3px solid #eff2f7;
  border-radius: 10px;
  box-sizing: border-box;
}

.poster.frame[lazy='loading'] {
  height: 6.4271428571rem;
  width: 11.4371428571rem;
  transition: all 0.3s ease;
  object-fit: contain;
  background: #fff;
}

.poster.frame[lazy='loaded'] {
  background: #000;
  transition: all 0.3s ease;
}

.content .poster.frame:hover {
  transform: scale(1.015);
  border-color: #20a0ff;
  border-radius: 8px;
}

/* Transition for poster frame. */
.poster-enter,
.poster-leave-to {
  opacity: 0;
  transform: scale(0.975) translatex(-4rem);
}

.poster-enter-to,
.poster-leave {
  opacity: 1;
  transform: scale(1) translateX(0);
}

.poster-enter-active,
.poster-leave-active,
.poster-move {
  /* transition: opacity 250ms, transform 250ms; */
  transition: all 250ms;
}

/* History transform */
.history-enter,
.history-leave-to {
  opacity: 0.01;
  transform: scale(0.975) translateY(-5rem);
}

.history-enter-to,
.history-leave {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.history-enter-active,
.history-leave-active,
.history-move {
  transition: all 250ms;
}

</style>
